import { productIsNewThresholdInDays } from '../../../constants';
import { calculateDifferenceInDays } from '../../../helpers/date';
import { generateUuid } from '../../../helpers/string';
import trans from '../../../helpers/trans';
import {
    Locales,
    ProductAttributeKey,
    ProductDimensionAttributeKey,
    ProductVariantResource,
} from '../../@api/Sylius';
import { transformBrandNameToLink } from '../../Brand/BrandTransformers';
import { transformToDateIsoString } from '../../Date/DateTransformers';
import { transformToDeliveryWindow } from '../../DeliveryWindow/DeliveryWindowTransformers';
import { transformToMarketingAction } from '../../MarketingAction/MarketingActionTransformers';
import { transformToProductPrice } from '../../Price/PriceTransformers';
import { transformToProductCode, transformToVariantCode } from '../Product/ProductTransformers';
import { ProductAttribute, ProductDimensionAttribute } from '../ProductAttribute/ProductAttribute';
import { transformToProductAttribute } from '../ProductAttribute/ProductAttributeTransformers';
import { ProductVariantColor } from '../ProductVariantColor/ProductVariantColor';
import { dimensionAttributeKeys, ProductVariant } from './ProductVariant';

export const locale = Locales.nl;

export const transformToProductVariant = (resource: ProductVariantResource): ProductVariant => {
    const productCode = transformToProductCode(resource.product);
    const variantId = transformToVariantCode(resource['@id']);

    const id = resource.id
        ? resource.id.toString()
        : generateUuid();

    const { description } = resource.translations[locale];

    const createdAtDate = resource.createdAt
        ? transformToDateIsoString(resource.createdAt)
        : false;

    const isNew = createdAtDate
        ? calculateDifferenceInDays(createdAtDate) <= productIsNewThresholdInDays
        : false;

    const price = transformToProductPrice(resource.price, resource.originalPrice, resource.discount || undefined);

    const marketingAction = resource.marketingAction
        ? transformToMarketingAction(resource.marketingAction)
        : undefined;

    const deliveryWindow = resource.deliveryWindow
        ? transformToDeliveryWindow(resource.deliveryWindow)
        : undefined;

    const deliveryNotice = deliveryWindow?.description;

    const translatedProductAttributes = resource.attributes
        ? resource.attributes.filter(attribute => attribute.localeCode === locale)
        : [];

    const productVariantAttributes = translatedProductAttributes
        ? translatedProductAttributes.map(transformToProductAttribute)
        : [];

    const productVariantAttributesWithData = productVariantAttributes.filter(
        productVariantAttribute => productVariantAttribute.value
            && !dimensionAttributeKeys.includes(productVariantAttribute.key as ProductAttributeKey),
    );

    const skuAttribute = {
        key: ProductAttributeKey.sku,
        label: trans('entities.product.sku'),
        value: variantId,
    };

    const attributes: ProductAttribute[] = [
        skuAttribute,
        ...productVariantAttributesWithData,
    ];

    const dimensionAttributeValues = Object.values(ProductDimensionAttributeKey);
    const dimensionAttributes: ProductDimensionAttribute[] = dimensionAttributeValues.map(attributeKey => {
        const foundAttribute = productVariantAttributes.find(productAttribute => productAttribute.key === attributeKey);

        return {
            key: attributeKey,
            label: foundAttribute?.label || '',
            value: foundAttribute?.value || '',
        };
    });

    const dimensionAttributesWithData = dimensionAttributes.filter(dimensionAttribute => dimensionAttribute.value);

    const attributesToExclude = [
        ...dimensionAttributeValues,
        ProductAttributeKey.brand,
        ProductAttributeKey.isNew,
        ProductAttributeKey.colli,
    ];

    const filteredAttributes = attributes.filter(productAttribute => (
        !attributesToExclude.includes(productAttribute.key)
    ));

    attributes.length = 0;
    attributes.push(...filteredAttributes);

    const brandAttribute = productVariantAttributesWithData.find(productAttribute => productAttribute.key === ProductAttributeKey.brand);
    const brand = brandAttribute
        ? transformBrandNameToLink(brandAttribute.value)
        : undefined;

    const colorsAttribute = productVariantAttributesWithData.find(productAttribute => productAttribute.key === ProductAttributeKey.colors);
    const colors = colorsAttribute
        ? colorsAttribute.value.split(', ') as ProductVariantColor[]
        : [];

    return {
        id,
        legacyId: resource.loods5IdLegacy?.toString(),
        variantId: resource.id?.toString() || '',
        variantCode: resource.code,
        name: resource.name || '',
        relativeUrl: resource.relativeUrl,
        description,
        isNew,
        price,
        marketingAction,
        deliveryNotice,
        attributes,
        dimensionAttributes: dimensionAttributesWithData,
        brand,
        colors,
        inStock: resource.inStock,
        stockQuantity: resource.onHand,
        productCode,
    };
};
